import { getUser } from '@frontend/auth-helpers';
import { createStoreWithSubscribe, createShallowStore } from '@frontend/store';

type PaymentsCalloutsState = {
  bulkCollectionsSelectAllDismissed: boolean;
  bulkCollectionsTabDismissed: boolean;
};

const STORAGE_KEY = 'payments:callouts';

const getStorageKey = (userId: string) => `${STORAGE_KEY}:${userId}`;

const getCalloutsState = (): PaymentsCalloutsState | null => {
  const user = getUser();
  if (!user?.userID) return null;

  try {
    const stored = localStorage.getItem(getStorageKey(user.userID));
    return stored ? JSON.parse(stored) : null;
  } catch {
    return null;
  }
};

const updateCalloutsState = (updates: Partial<PaymentsCalloutsState>) => {
  const user = getUser();
  if (!user?.userID) return;

  try {
    const current = getCalloutsState() || {
      bulkCollectionsSelectAllDismissed: false,
      bulkCollectionsTabDismissed: false,
    };
    const updated = { ...current, ...updates };
    localStorage.setItem(getStorageKey(user.userID), JSON.stringify(updated));
  } catch {
    // Fail silently
  }
};

export type BulkCollectionsUIStore = {
  shouldShowSelectAllCallout: boolean;
  setShouldShowSelectAllCallout: (show: boolean) => void;
  dismissSelectAllCallout: () => void;
  resetSelectAllCallout: () => void;
  shouldShowTabCallout: boolean;
  setShouldShowTabCallout: (show: boolean) => void;
  dismissTabCallout: () => void;
};

const getInitialState = () => {
  const stored = getCalloutsState();
  return {
    shouldShowSelectAllCallout: stored ? !stored.bulkCollectionsSelectAllDismissed : false,
    shouldShowTabCallout: stored ? !stored.bulkCollectionsTabDismissed : false,
  };
};

export const useBulkCollectionsUIStore = createStoreWithSubscribe<BulkCollectionsUIStore>(
  (set) => ({
    ...getInitialState(),
    setShouldShowSelectAllCallout: (show: boolean) => {
      // Don't show if permanently dismissed
      const stored = getCalloutsState();
      if (stored?.bulkCollectionsSelectAllDismissed) return;
      set({ shouldShowSelectAllCallout: show });
    },
    dismissSelectAllCallout: () => {
      updateCalloutsState({ bulkCollectionsSelectAllDismissed: true });
      set({ shouldShowSelectAllCallout: false });
    },
    resetSelectAllCallout: () => {
      updateCalloutsState({ bulkCollectionsSelectAllDismissed: false });
      set({ shouldShowSelectAllCallout: true });
    },
    setShouldShowTabCallout: (show: boolean) => {
      // Don't show if permanently dismissed
      const stored = getCalloutsState();
      if (stored?.bulkCollectionsTabDismissed) return;
      set({ shouldShowTabCallout: show });
    },
    dismissTabCallout: () => {
      updateCalloutsState({ bulkCollectionsTabDismissed: true });
      set({ shouldShowTabCallout: false });
    },
  }),
  {
    name: 'BulkCollectionsUIStore',
    trace: true,
  }
);

export const useBulkCollectionsUIShallowStore = createShallowStore(useBulkCollectionsUIStore);
